import React, { useEffect, useMemo, useRef, useState } from "react";

const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

const loadTurnstile = (() => {
  let promise;
  return () => {
    if (typeof window === "undefined") return Promise.reject(new Error("Turnstile unavailable"));
    if (window.turnstile && typeof window.turnstile.render === "function") return Promise.resolve(window.turnstile);
    if (promise) return promise;
    promise = new Promise((resolve, reject) => {
      let settled = false;
      const finish = (fn, value) => {
        if (settled) return;
        settled = true;
        fn(value);
      };
      const finalizeResolve = (value) => finish(resolve, value);
      const finalizeReject = (error) => finish(reject, error);
      const ensureTurnstile = () => {
        if (window.turnstile && typeof window.turnstile.render === "function") {
          finalizeResolve(window.turnstile);
          return true;
        }
        return false;
      };
      if (ensureTurnstile()) return;

      const timeoutId = setTimeout(() => {
        finalizeReject(new Error("Turnstile load timeout"));
      }, 10000);

      const onLoad = () => {
        clearTimeout(timeoutId);
        if (ensureTurnstile()) return;
        finalizeReject(new Error("Turnstile unavailable"));
      };
      const onError = () => {
        clearTimeout(timeoutId);
        finalizeReject(new Error("Turnstile script failed"));
      };

      const existing = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
      if (existing) {
        existing.addEventListener("load", onLoad, { once: true });
        existing.addEventListener("error", onError, { once: true });
        return;
      }
      const script = document.createElement("script");
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = onLoad;
      script.onerror = onError;
      document.head.appendChild(script);
    }).catch((err) => {
      promise = undefined;
      throw err;
    });
    return promise;
  };
})();

export default function TurnstileWidget({
  siteKey,
  onToken,
  onError,
  className = "",
  action,
  size = "normal",
  execution = "execute",
  appearance = "execute",
  autoExecute = true,
}) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  const executingRef = useRef(false);
  const executedRef = useRef(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const opts = useMemo(
    () => ({
      sitekey: siteKey,
      callback: (token) => {
        executingRef.current = false;
        executedRef.current = true;
        onTokenRef.current?.(token);
      },
      "expired-callback": () => {
        executingRef.current = false;
        executedRef.current = false;
        onTokenRef.current?.("");
      },
      "error-callback": () => {
        executingRef.current = false;
        executedRef.current = false;
        onTokenRef.current?.("");
        onErrorRef.current?.("turnstile_error");
      },
      ...(size ? { size } : {}),
      ...(execution ? { execution } : {}),
      ...(appearance ? { appearance } : {}),
      ...(action ? { action } : {}),
    }),
    [action, appearance, execution, siteKey, size]
  );

  useEffect(() => {
    let cancelled = false;
    if (!siteKey) return;
    setReady(false);
    executingRef.current = false;
    executedRef.current = false;

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled) return;
        if (!containerRef.current) return;
        if (!turnstile || typeof turnstile.render !== "function") {
          onErrorRef.current?.("turnstile_unavailable");
          return;
        }

        if (widgetIdRef.current !== null) {
          try {
            turnstile.remove(widgetIdRef.current);
          } catch (_err) {
            // ignore
          }
          widgetIdRef.current = null;
        }

        try {
          containerRef.current.innerHTML = "";
        } catch (_err) {
          // ignore
        }

        try {
          widgetIdRef.current = turnstile.render(containerRef.current, opts);
        } catch (_err) {
          onErrorRef.current?.("turnstile_render_failed");
          return;
        }
        setReady(true);

        if (!autoExecute) return;
        if (execution !== "execute") return;
        const widgetId = widgetIdRef.current;
        if (widgetId === null || widgetId === undefined) return;
        if (executingRef.current) return;
        executingRef.current = true;

        setTimeout(() => {
          if (cancelled) return;
          if (!turnstile || typeof turnstile.execute !== "function") {
            executingRef.current = false;
            onErrorRef.current?.("turnstile_execute_unavailable");
            return;
          }
          if (widgetIdRef.current !== widgetId) return;
          try {
            if (typeof turnstile.reset === "function" && executedRef.current) {
              turnstile.reset(widgetId);
              executedRef.current = false;
            }
          } catch (_err) {
            // ignore
          }
          try {
            turnstile.execute(widgetId);
          } catch (_err) {
            executingRef.current = false;
            onErrorRef.current?.("turnstile_execute_failed");
          }
        }, 0);
      })
      .catch((err) => {
        if (!cancelled) {
          setReady(false);
          onErrorRef.current?.(String(err?.message || "turnstile_load_failed"));
        }
      });

    return () => {
      cancelled = true;
      const id = widgetIdRef.current;
      widgetIdRef.current = null;
      if (window.turnstile && id !== null) {
        try {
          window.turnstile.remove(id);
        } catch (_err) {
          // ignore
        }
      }
    };
  }, [autoExecute, execution, opts, siteKey]);

  if (!siteKey) return null;

  return (
    <div className={className}>
      <div ref={containerRef} />
      {!ready && <div className="turnstile-hint">Loading security check</div>}
    </div>
  );
}
